import { useCallback, useEffect, useState } from 'react';

import { useBrand } from '@/shared/hooks/useBrand/useBrand';
import { useAppSettingsQuery } from '@/shared/services/app-settings/app-settings.api';
import { applyAccentToDocument } from '@/shared/utils/accent-override/accent-override.util';
import {
  applyThemeToDocument,
  resolveTheme,
} from '@/shared/utils/preferences-bootstrap/preferences-bootstrap.util';
import {
  selectThemePreference,
  themeChanged,
} from '@/shared/store/reducers/preferences/preferences.slice';
import {
  type ResolvedTheme,
  type ThemePreference,
} from '@/shared/store/reducers/preferences/preferences.types';
import { useAppDispatch, useAppSelector } from '@/shared/store/typed-hooks/typed-hooks';

import { type ThemeProviderLogic } from './ThemeProvider.types';

const DARK_QUERY = '(prefers-color-scheme: dark)';

/**
 * Keeps the painted theme in step with the operator's preference AND with the
 * operating system.
 *
 * The OS half is why this is a subscription rather than a one-off read: an
 * operator on `system` who works past sunset should follow their machine into
 * dark mode without reloading, and that is the entire reason `system` is a
 * distinct stored value instead of being resolved away at write time.
 *
 * The listener is attached whatever the preference and its result is ignored
 * unless the preference is `system`. Attaching conditionally would tear the
 * subscription down and rebuild it on every preference change for no gain.
 *
 * The brand's accent rides along (P28): this provider never unmounts, so it is
 * THE subscription to `/app-settings` — the one request that keeps the payload
 * cached for every `useBrand()` reader in the tree — and the override is
 * re-applied whenever the accent OR the theme changes, since the ink depends on
 * both.
 */
export function useThemeProviderLogic(): ThemeProviderLogic {
  const dispatch = useAppDispatch();
  const preference = useAppSelector(selectThemePreference);
  useAppSettingsQuery();
  const { accentColor } = useBrand();
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(() => resolveTheme('system'));

  useEffect(() => {
    let query: MediaQueryList;
    try {
      query = window.matchMedia(DARK_QUERY);
    } catch {
      return undefined;
    }

    const onChange = (event: MediaQueryListEvent): void => {
      setSystemTheme(event.matches ? 'dark' : 'light');
    };

    query.addEventListener('change', onChange);
    return () => {
      query.removeEventListener('change', onChange);
    };
  }, []);

  const theme: ResolvedTheme = preference === 'system' ? systemTheme : preference;

  useEffect(() => {
    applyThemeToDocument(theme);
  }, [theme]);

  useEffect(() => {
    applyAccentToDocument(accentColor, theme);
  }, [accentColor, theme]);

  const setPreference = useCallback(
    (next: ThemePreference) => {
      dispatch(themeChanged(next));
    },
    [dispatch],
  );

  /**
   * Toggling commits an EXPLICIT preference — the opposite of what is on screen
   * — rather than cycling light → dark → system. An operator who clicks the
   * moon wants dark now; offering them a third state they did not ask for is the
   * kind of cleverness that makes a control feel broken. `system` is chosen
   * deliberately, from the settings surface.
   */
  const toggle = useCallback(() => {
    dispatch(themeChanged(theme === 'dark' ? 'light' : 'dark'));
  }, [dispatch, theme]);

  return { preference, theme, setPreference, toggle };
}
